import { useMemo } from 'react';

import { useLingui } from '@lingui/react/macro';

import type { TProcedure } from '@/react-query/useVisitSummaries';

import { type TProcedureWithAddressed } from './utils';

type RecommendationCategory = 'restorative' | 'incipient' | 'periodontal';

interface Props {
  recommendedProcedures?: TProcedureWithAddressed[];
}

export const BasicOdontogramRecommendations = ({
  recommendedProcedures,
}: Props) => {
  const { t } = useLingui();

  const recommendationCategoryToCopy: Record<RecommendationCategory, string> = {
    restorative: t`Restorative`,
    incipient: t`Incipient`,
    periodontal: t`Periodontal`,
  };

  const basicOdontogramRecommendations = useMemo(() => {
    const acc = new Set<RecommendationCategory>();

    recommendedProcedures?.forEach(({ name }) => {
      const restorative: TProcedure['name'][] = [
        'crown',
        'extraction',
        'filling',
        'implant',
        'rootCanal',
        'pulpotomy',
      ];
      const incipient: TProcedure['name'][] = ['enamelRegen'];
      const periodontal: TProcedure['name'][] = ['perio', 'locPerio'];

      if (restorative.includes(name)) {
        acc.add('restorative');
      } else if (incipient.includes(name)) {
        acc.add('incipient');
      } else if (periodontal.includes(name)) {
        acc.add('periodontal');
      }
    });

    return acc;
  }, [recommendedProcedures]);

  if (!recommendedProcedures?.length) return null;

  return (
    <ul>
      {[...basicOdontogramRecommendations].map((recommendationCategory) => {
        const recommendationCategoryCopy =
          recommendationCategoryToCopy[recommendationCategory];
        return (
          <li
            className="vai-font-inter vai-font-semibold vai-text-sm"
            key={recommendationCategory}
          >
            {recommendationCategoryCopy}
          </li>
        );
      })}
    </ul>
  );
};
